import { useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse';
import { RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { InputFileHandle } from 'src/components/inputs/InputFile';
import { useForecastSession } from 'src/features/forecastSessions/useForecastSession';
import {
  getForecastSessionsQueryKey,
  getSessionForecastDataQueryPrefix,
  getSessionProductsQueryKey,
} from 'src/keys';
import { useAddProductsToSession } from 'src/services/api/addProductsToOpenSession';
import { useDeleteProductsFromSession } from 'src/services/api/deleteProductsFromOpenSession';
import { useBoundStore } from 'src/store/store';

export function useConfigurationCriteriaProducts(
  scopePurchaseProducts: string[],
  inputFileRef: RefObject<InputFileHandle>,
) {
  const { t } = useTranslation('configuration');
  const { activeSession } = useForecastSession();
  const { validSkus, setValidSkus, setInvalidSkus, clearParsedSkus } =
    useBoundStore();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { mutate: addProductsToSession } = useAddProductsToSession();
  const { mutate: deleteProductsFromSession } = useDeleteProductsFromSession();
  const sessionId = activeSession?.id;

  const handleFileUpload = (file: File) => {
    setOpen(true);
    setLoading(true);
    clearParsedSkus();
    Papa.parse(file, {
      delimiter: ' ',
      skipEmptyLines: true,
      complete: (results) => {
        setLoading(false);
        if (results.errors.length) {
          toast.error(t('Error'), {
            description: t('Error while parsing the csv file'),
          });
          inputFileRef.current?.clear();
          return;
        }
        const parsedData = results.data as [];
        const newValidSkus: string[] = [];
        const newInvalidSkus: string[] = [];
        parsedData.forEach((item) => {
          if (scopePurchaseProducts.includes(item?.[0])) {
            newValidSkus.push(item?.[0]);
          } else {
            newInvalidSkus.push(item?.[0]);
          }
        });
        setValidSkus(newValidSkus);
        setInvalidSkus(newInvalidSkus);
      },
      error: () => {
        setLoading(false);
        toast.error(t('Error'), {
          description: t('Error while parsing the csv file'),
        });
        inputFileRef.current?.clear();
      },
    });
  };

  const handleSubmit = () => {
    if (!sessionId) {
      return;
    }

    setOpen(false);
    const length = validSkus.length;
    inputFileRef.current?.clear();
    addProductsToSession(
      { sessionId, skus: validSkus },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getSessionProductsQueryKey(sessionId),
          });
          queryClient.invalidateQueries({
            queryKey: getSessionForecastDataQueryPrefix(sessionId),
          });
          queryClient.invalidateQueries({
            queryKey: getForecastSessionsQueryKey(),
          });
          toast.success(t('Success'), {
            description: `${length} ${t('product imported successfully')}`,
            action: {
              label: t('Cancel'),
              onClick: () => {
                deleteProductsFromSession(
                  { sessionId, skus: validSkus },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries({
                        queryKey: getSessionProductsQueryKey(sessionId),
                      });
                      queryClient.invalidateQueries({
                        queryKey: getSessionForecastDataQueryPrefix(sessionId),
                      });
                      queryClient.invalidateQueries({
                        queryKey: getForecastSessionsQueryKey(),
                      });
                    },
                  },
                );
                toast.info(t('Import cancelled'), {
                  description: t('The imported products have been removed'),
                });
              },
            },
          });
        },
        onError: () => {
          toast.error(t('Error'), {
            description: t('Error while importing products'),
            action: {
              label: t('Retry'),
              onClick: () => {
                handleSubmit();
              },
            },
          });
        },
      },
    );
  };

  const handleCancel = () => {
    setOpen(false);
    inputFileRef.current?.clear();
    toast(t('Cancel'), {
      description: t('Import cancelled'),
    });
  };

  return {
    loading,
    open,
    setOpen,
    handleFileUpload,
    handleSubmit,
    handleCancel,
  };
}
